import { Box, Button, Stack, Typography } from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import { FormRowSwitch, FormSection, SelectInput, TextInput } from "components";
import { apiURL } from "config/constants";
import { incidentDocsBcOn, incidentDocsOn, useIncidentDocs, useIncidentDocsBc } from "incidents/feature";
import { SiteJurisdiction } from "incidents/types";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePhrase } from "../incidents/common";

const EMPTY: SiteJurisdiction = {
  province: "other",
  overdueGraceMinutes: 60,
  residentNamesAllowed: true,
  funded: false,
  aiDocPolicyAck: false
};

export function IncidentJurisdictionPanel() {
  const phrase = usePhrase();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const docs = incidentDocsOn(useIncidentDocs());
  const bcOn = incidentDocsBcOn(useIncidentDocsBc());
  const [value, setValue] = useState<SiteJurisdiction>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const siteId = window.location.pathname.split("/")[2] || "";

  useEffect(() => {
    if (!docs || !siteId) return;
    let cancelled = false;
    fetch(`${apiURL}/incidents/sites/${siteId}/jurisdiction`, { credentials: "include" })
      .then(async (response) => {
        const json = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(json?.message || "Could not load the home setting.");
        return json?.data?.jurisdiction || json?.jurisdiction || EMPTY;
      })
      .then((jurisdiction: SiteJurisdiction) => {
        if (!cancelled) setValue({ ...EMPTY, ...jurisdiction });
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [docs, siteId]);

  if (!docs) return null;

  function patch(next: Partial<SiteJurisdiction>) {
    setValue((current) => ({ ...current, ...next }));
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`${apiURL}/incidents/sites/${siteId}/jurisdiction`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(value)
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) {
        const messages = json?.data?.errors || json?.errors;
        throw new Error(messages?.[0] || json?.message || "Could not save the home setting.");
      }
      if (json?.data?.jurisdiction) setValue({ ...EMPTY, ...json.data.jurisdiction });
      enqueueSnackbar(
        phrase(
          "Saved for new alerts. Existing records keep the setting they had when the alert was created.",
          "Enregistré pour les nouvelles alertes. Les dossiers existants gardent le réglage du moment de l'alerte."
        ),
        { variant: "success" }
      );
      navigate("/sites");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not save the home setting.";
      setError(message);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  }

  const bc = value.province === "BC";
  const provinceOptions = [
    { label: phrase("ON (Ontario)", "ON (Ontario)"), value: "ON" },
    { label: phrase("QC (Quebec)", "QC (Québec)"), value: "QC" },
    ...(bcOn ? [{ label: phrase("BC (British Columbia)", "BC (Colombie-Britannique)"), value: "BC" }] : []),
    { label: phrase("Other", "Autre"), value: "other" }
  ];

  return (
    <FormSection
      title={phrase("Incident Documentation", "Documentation des incidents")}
      description={phrase(
        "A facility admin sets the province and the care setting. Nurses confirm them. They cannot replace them on a record.",
        "Un administrateur fixe la province et le milieu de soins. Le personnel les confirme. Il ne peut pas les remplacer sur un dossier."
      )}
    >
      <Stack spacing={2} maxWidth={640}>
        <SelectInput
          label={phrase("Province", "Province")}
          value={value.province}
          data={provinceOptions}
          onChange={(event) => patch({ province: event.target.value as SiteJurisdiction["province"] })}
          fullWidth
        />
        {bc ? (
          <>
            <SelectInput
              label={phrase("Care setting", "Milieu de soins")}
              value={value.bcLicenceType || ""}
              data={[
                { label: phrase("Choose", "Choisir"), value: "" },
                { label: phrase("Long-term care", "Soins de longue durée"), value: "LTC" },
                { label: phrase("Assisted living", "Résidence avec assistance"), value: "AL" }
              ]}
              onChange={(event) =>
                patch({ bcLicenceType: (event.target.value || null) as SiteJurisdiction["bcLicenceType"] })
              }
              fullWidth
            />
            <SelectInput
              label={phrase("Health authority", "Autorité sanitaire")}
              value={value.healthAuthority || ""}
              data={[
                { label: phrase("Choose", "Choisir"), value: "" },
                { label: "Fraser Health", value: "FHA" },
                { label: "Interior Health", value: "IHA" },
                { label: "Island Health", value: "VIHA" },
                { label: "Northern Health", value: "NHA" },
                { label: "Vancouver Coastal Health", value: "VCH" },
                { label: "Providence Health Care", value: "PHC" }
              ]}
              onChange={(event) =>
                patch({ healthAuthority: (event.target.value || null) as SiteJurisdiction["healthAuthority"] })
              }
              fullWidth
            />
            <TextInput
              label={phrase("Licence or registration number", "Numéro de permis ou d'inscription")}
              value={value.licenceNumber || ""}
              onChange={(event) => patch({ licenceNumber: event.target.value })}
              fullWidth
            />
            <FormRowSwitch
              outlined
              label={phrase("This home is publicly funded", "Ce milieu est financé par un programme public")}
              checked={Boolean(value.funded)}
              onChange={(checked) => patch({ funded: checked })}
            />
            <TextInput
              type="number"
              label={phrase(
                "Minutes before an immediate duty shows as overdue (site policy)",
                "Minutes avant qu'un devoir immédiat soit en retard (politique du site)"
              )}
              value={String(value.overdueGraceMinutes ?? 60)}
              onChange={(event) => patch({ overdueGraceMinutes: Number(event.target.value) })}
              helperText={phrase(
                "Default 60. The regulation says immediately and does not set this number.",
                "60 par défaut. Le règlement dit immédiatement et ne fixe pas ce nombre."
              )}
              fullWidth
            />
            <TextInput
              label={phrase(
                "Post-fall schedule name, from this home's policy",
                "Nom de l'horaire post-chute, selon la politique du milieu"
              )}
              value={value.postFallProtocol?.name || ""}
              onChange={(event) => patch({ postFallProtocol: { ...value.postFallProtocol, name: event.target.value } })}
              helperText={phrase(
                "Leave blank if the home has no schedule. Do not invent an interval.",
                "Laissez vide s'il n'y a pas d'horaire. N'inventez pas d'intervalle."
              )}
              fullWidth
            />
            <TextInput
              label={phrase("Post-fall schedule version", "Version de l'horaire post-chute")}
              value={value.postFallProtocol?.version || ""}
              onChange={(event) =>
                patch({ postFallProtocol: { ...value.postFallProtocol, version: event.target.value } })
              }
              fullWidth
            />
            <TextInput
              label={phrase("Designated agency phone (optional)", "Téléphone de l'organisme désigné (facultatif)")}
              value={value.agaAgencyPhone || ""}
              onChange={(event) => patch({ agaAgencyPhone: event.target.value })}
              fullWidth
            />
            <FormRowSwitch
              outlined
              label={phrase(
                "This home has an employer policy for AI-assisted documentation. The nurse remains accountable.",
                "Ce milieu a une politique de l'employeur pour la documentation assistée. L'infirmière reste responsable."
              )}
              checked={value.aiDocPolicyAck === true}
              onChange={(checked) => patch({ aiDocPolicyAck: checked })}
            />
          </>
        ) : null}
        <FormRowSwitch
          outlined
          label={phrase("Store resident names on incident records", "Conserver les noms des usagers sur les dossiers d'incident")}
          description={phrase(
            "Turn this off for homes that keep names in their clinical system.",
            "Décochez pour les milieux qui gardent les noms dans leur système clinique."
          )}
          checked={value.residentNamesAllowed !== false}
          onChange={(checked) => patch({ residentNamesAllowed: checked })}
        />
        {error ? (
          <Typography color="error" role="alert">
            {error}
          </Typography>
        ) : null}
        <Box>
          <Button
            type="button"
            variant="contained"
            disabled={busy}
            onClick={save}
            sx={{
              ...primaryContainedButtonSx,
              minWidth: 180,
              height: 44,
              textTransform: "none",
              fontWeight: 600
            }}
          >
            {phrase("Save incident setting", "Enregistrer le réglage d'incident")}
          </Button>
        </Box>
      </Stack>
    </FormSection>
  );
}
